/**
 * Utility functions for handling conditional questionnaire logic
 * Allows sections/questions to be shown/hidden based on previous answers
 */

export interface Condition {
  questionId?: string;
  operator?: string;
  value?: unknown;
}

export interface Conditional {
  questionId?: string;
  operator?: string;
  value?: unknown;
  logic?: 'AND' | 'OR';
  conditions?: Condition[];
}

export interface Question {
  id: string;
  question?: string;
  description?: string;
  type?: string;
  options?: string[];
  audience?: string;
  conditional?: Conditional;
}

export interface Subsection {
  id?: string;
  title?: string;
  questions?: Question[];
  audience?: string;
  conditional?: Conditional;
}

export interface Section {
  id?: string;
  title?: string;
  sections: Subsection[];
}

export type Answers = Record<string, unknown>;

/**
 * Evaluates a condition against current answers
 */
export const evaluateCondition = (condition: Condition | null | undefined, answers: Answers): boolean => {
  if (!condition?.questionId) {
    return true; // No condition means always show
  }

  const answer = answers[condition.questionId];
  const { operator, value } = condition;

  // If no answer yet, condition is not met (except for notEquals/notIn)
  if (answer == null || answer === '') {
    return operator === 'notEquals' || operator === 'notIn';
  }

  switch (operator) {
    case 'equals':
      return answer === value;

    case 'notEquals':
      return answer !== value;

    case 'contains':
      // For multiple choice answers (arrays)
      if (Array.isArray(answer)) {
        return answer.includes(value as string);
      }
      // For string answers
      return String(answer).includes(String(value));

    case 'notContains':
      if (Array.isArray(answer)) {
        return !answer.includes(value as string);
      }
      return !String(answer).includes(String(value));

    case 'in':
      // Check if answer is in the list of values
      if (!Array.isArray(value)) {
        return false;
      }
      return (value as unknown[]).includes(answer);

    case 'notIn':
      if (!Array.isArray(value)) {
        return true;
      }
      return !(value as unknown[]).includes(answer);

    case 'greaterThan':
      return Number(answer) > Number(value);

    case 'lessThan':
      return Number(answer) < Number(value);

    case 'answered':
      // Check if question has been answered (any non-empty value)
      if (Array.isArray(answer)) {
        return answer.length > 0;
      }
      return answer !== '' && answer != null;

    case 'notAnswered':
      if (Array.isArray(answer)) {
        return answer.length === 0;
      }
      return answer === '' || answer == null;

    default:
      return true;
  }
};

/**
 * Evaluates multiple conditions with AND/OR logic
 */
export const evaluateConditional = (conditional: Conditional | null | undefined, answers: Answers): boolean => {
  if (!conditional) {
    return true; // No conditional means always show
  }

  // Handle single condition (backward compatibility)
  if (conditional.questionId) {
    return evaluateCondition(conditional, answers);
  }

  // Handle multiple conditions
  if (conditional.conditions && Array.isArray(conditional.conditions)) {
    const logic = conditional.logic || 'AND';
    const results = conditional.conditions.map(cond => evaluateCondition(cond, answers));

    if (logic === 'OR') {
      return results.some(result => result === true);
    } else {
      // Default to AND
      return results.every(result => result === true);
    }
  }

  return true;
};

/**
 * Filters questionnaire sections based on conditional logic
 */
export const filterConditionalSections = (questionnaire: Section[] | null | undefined, answers: Answers): Section[] => {
  if (!questionnaire || !Array.isArray(questionnaire)) {
    return questionnaire ?? [];
  }

  return questionnaire.map(section => ({
    ...section,
    sections: section.sections.filter(subsection => {
      // Check if subsection has conditional logic
      if (subsection.conditional) {
        return evaluateConditional(subsection.conditional, answers);
      }
      return true; // No conditional means always show
    })
  })).filter(section => section.sections.length > 0); // Remove sections with no visible subsections
};

/**
 * Gets the total count of visible subsections after applying conditionals
 */
export const getTotalVisibleSubsections = (questionnaire: Section[] | null | undefined, answers: Answers): number => {
  const filtered = filterConditionalSections(questionnaire, answers);
  return filtered.reduce((total, section) => total + section.sections.length, 0);
};

/**
 * Filters questionnaire subsections based on user persona (audience field).
 */
export const filterQuestionnaireByPersona = (sections: Section[] | null | undefined, persona: string | null): Section[] => {
  if (!sections || !Array.isArray(sections)) return sections || [];
  if (!persona || persona === 'both') return sections;

  return sections.map(section => ({
    ...section,
    sections: (section.sections || []).filter(subsection => {
      const audience = subsection.audience;
      // Show subsection if: no audience set, audience is 'all', or matches persona
      return !audience || audience === 'all' || audience === persona;
    }).map(subsection => {
      // Filter individual questions by audience within visible subsections
      if (!subsection.questions) return subsection;
      const filteredQuestions = subsection.questions.filter(q => {
        return !q.audience || q.audience === 'all' || q.audience === persona;
      });
      if (filteredQuestions.length === subsection.questions.length) return subsection;
      return { ...subsection, questions: filteredQuestions };
    }).filter(subsection => (subsection.questions || []).length > 0)
  })).filter(section => (section.sections || []).length > 0);
};

interface MissingSection {
  sectionTitle: string | undefined;
  subsectionTitle: string | undefined;
  subsectionId: string | undefined;
}

/**
 * Gets subsection IDs that belong to a specific persona but are missing answers.
 * Used for the "missing sections" screen.
 */
export const getMissingSections = (sections: Section[] | null | undefined, otherPersona: string, answers: Answers): MissingSection[] => {
  if (!sections || !Array.isArray(sections)) return [];

  const missing: MissingSection[] = [];
  sections.forEach(section => {
    (section.sections || []).forEach(subsection => {
      const audience = subsection.audience;
      // Check subsection-level audience and also questions with the other persona's audience
      const subsectionMatchesOther = audience === otherPersona;
      const hasOtherPersonaQuestions = (subsection.questions || []).some(q => q.audience === otherPersona);
      if (!subsectionMatchesOther && !hasOtherPersonaQuestions) return;

      // Only check questions that belong to the other persona
      const relevantQuestions = (subsection.questions || []).filter(q => {
        if (q.audience === otherPersona) return true;
        if (subsectionMatchesOther && (!q.audience || q.audience === 'all')) return true;
        return false;
      });

      const hasUnanswered = relevantQuestions.some(q => {
        const answer = answers[q.id];
        if (answer == null || answer === '') return true;
        if (Array.isArray(answer) && answer.length === 0) return true;
        return false;
      });

      if (hasUnanswered) {
        missing.push({
          sectionTitle: section.title,
          subsectionTitle: subsection.title,
          subsectionId: subsection.id,
        });
      }
    });
  });
  return missing;
};

/**
 * Adaptive filtering for questionnaire based on key answers
 * This provides more advanced filtering logic than simple conditionals
 */
export const filterQuestionnaireByAnswers = (sections: Section[] | null | undefined, currentAnswers: Answers): Section[] => {
  if (!sections || sections.length === 0) return sections || [];

  // Get the adaptive question answers
  const aiImplementationStage = (currentAnswers['ai_implementation_stage'] as string) || '';
  const dataPipelinesCount = (currentAnswers['data_pipelines_count'] as string) || '';
  const aiMlUseCases = (currentAnswers['ai_ml_use_cases'] as string[]) || [];

  // Define traditional ML use cases
  const traditionalMLUseCases = new Set([
    'Fraud detection',
    'Churn prediction',
    'Forecasting / Demand planning',
    'Predictive maintenance',
    'Recommendation systems',
    'Time series analysis',
    'Risk modeling / Credit scoring'
  ]);

  // Define GenAI use cases
  const genAIUseCases = new Set([
    'Conversational AI / Chatbots',
    'Content generation (text, images, code)',
    'Document analysis / Summarization',
    'Question answering systems',
    'AI Agents / Autonomous assistants',
    'Search / Semantic search',
    'Customer support automation'
  ]);

  // Determine if user selected traditional ML or GenAI
  const hasTraditionalML = aiMlUseCases.some(uc => traditionalMLUseCases.has(uc));
  const hasGenAI = aiMlUseCases.some(uc => genAIUseCases.has(uc));

  // Determine if user has AI workloads in PoC or Production
  const hasAIWorkloads = currentAnswers['ai_pocs_count'] !== '0' ||
                        currentAnswers['ai_production_workflows_count'] !== '0';

  // Determine if we should hide AI maturity questions
  const isEarlyStage = !aiImplementationStage ||
                       String(aiImplementationStage).includes('Exploring') ||
                       String(aiImplementationStage).includes('Planning');


  // Determine which sections to skip
  const skipDataOps = dataPipelinesCount === '0';
  const skipAISections = isEarlyStage ||
                        (currentAnswers['ai_pocs_count'] === '0' &&
                         currentAnswers['ai_production_workflows_count'] === '0');

  // Deep copy sections and filter questions
  const filteredSections = sections.map((section): Section | null => {
    if (skipDataOps && section.id === 'dataops_readiness') {
      return null;
    }
    if (skipAISections && section.id === 'genaiops_readiness') {
      return null;
    }
    if (skipAISections && section.id === 'agentops_readiness') {
      return null;
    }

    // Filter questions within sections
    const filteredSection = { ...section };
    if (filteredSection.sections) {
      filteredSection.sections = filteredSection.sections.map((subsection): Subsection | null => {
        // Handle conditional sections for C3 (Feature Store) and C3-ALT (RAG/GenAI)
        if (section.id === 'dataops_readiness') {
          // C3: Feature Store (Traditional ML, requires AI workloads)
          if (subsection.id === 'feature_engineering_store') {
            if (!hasTraditionalML) {
              return null; // Skip Feature Store if no traditional ML
            }
            if (!hasAIWorkloads) {
              return null; // Skip Feature Store if no AI workloads
            }
          }
          // C3-ALT: RAG & Knowledge Bases (GenAI, requires AI workloads)
          if (subsection.id === 'rag_knowledge_bases') {
            if (!hasGenAI) {
              return null; // Skip RAG if no GenAI
            }
            if (!hasAIWorkloads) {
              return null; // Skip RAG if no AI workloads
            }
          }
        }

        const filteredSubsection = { ...subsection };
        if (filteredSubsection.questions) {
          filteredSubsection.questions = filteredSubsection.questions.filter(question => {
            // Hide AI maturity questions if in early stage
            if (isEarlyStage &&
                (question.id === 'ai_pocs_count' ||
                 question.id === 'ai_production_workflows_count')) {
              return false;
            }
            return true;
          });
        }
        return filteredSubsection;
      }).filter((subsection): subsection is Subsection => subsection !== null); // Remove null subsections
    }
    return filteredSection;
  }).filter((section): section is Section => section !== null);

  return filteredSections;
};
